import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

type JwtClaims = Record<string, any>;

const defaultAccountClaimNames = [
  'http://promobid.promob.com/identity/security/claims/defaultaccountid',
  'defaultaccountid',
  'defaultAccountId',
  'default_account_id',
  'default_accountid'
];

const accountClaimNames = [
  'http://promobid.promob.com/identity/security/claims/accountid',
  'accountid',
  'accountId',
  'accountID',
  'account_id',
  'accounts',
  'accountIds',
  'account_ids'
];

const accountScopedSessionStorageKeys = [
  'systems',
  'account',
  'userPermissions',
  'EnableConfiguration',
  'Configuration',
  'accountUserList'
];

@Injectable()
export class AccountContextService {
  readonly accountKey = 'acc';

  private selectedAccount = new BehaviorSubject<string>("");
  private accountChangedSubject = new Subject<string>();
  private userAccounts: string[] = [];
  private defaultAccount: string = "";

  selectedAccount$ = this.selectedAccount.asObservable();
  accountChanged$ = this.accountChangedSubject.asObservable();

  initializeFromClaims(claims: JwtClaims[]): void {
    const previousSelectedAccount = this.getSelectedAccountId();
    const storedAccount = localStorage.getItem(this.accountKey);
    const storedAccountId = this.normalizeAccountId(storedAccount);

    this.userAccounts = this.normalizeAccountIds(this.getClaimValues(claims, accountClaimNames));
    this.defaultAccount = this.resolveDefaultAccount(claims);

    let accountKey = storedAccountId;
    const storedAccountIsInvalid = this.hasStoredAccount(storedAccount) && !storedAccountId;
    const storedAccountIsUnavailable = !!storedAccountId && this.userAccounts.indexOf(storedAccountId) === -1;

    if (!accountKey || storedAccountIsUnavailable) {
      accountKey = this.defaultAccount;
    }

    const shouldClearAccountScopedStorage =
      storedAccountIsInvalid ||
      storedAccountIsUnavailable ||
      (!storedAccountId && !!accountKey) ||
      (!!previousSelectedAccount && previousSelectedAccount !== accountKey);

    if (!this.selectAccount(accountKey, false)) {
      localStorage.removeItem(this.accountKey);
      this.selectedAccount.next("");
      this.clearAccountScopedSessionStorage();
      return;
    }

    if (shouldClearAccountScopedStorage) {
      this.clearAccountScopedSessionStorage();
    }
  }

  setSelectedAccountId(id: string): boolean {
    return this.selectAccount(id, true);
  }

  getSelectedAccountId(): string {
    return this.normalizeAccountId(this.selectedAccount.value);
  }

  getAccounts(): string[] {
    return [...this.userAccounts];
  }

  getDefaultAccountId(): string {
    return this.defaultAccount || "";
  }

  clear(): void {
    this.userAccounts = [];
    this.defaultAccount = "";
    this.selectedAccount.next("");
    this.clearAccountStorage();
  }

  private resolveDefaultAccount(claims: JwtClaims[]): string {
    const defaultAccountIds = this.normalizeAccountIds(this.getClaimValues(claims, defaultAccountClaimNames));
    const defaultAccountId = defaultAccountIds.find(accountId => this.userAccounts.includes(accountId));

    return defaultAccountId || this.userAccounts[0] || "";
  }

  private selectAccount(id: string, emitAccountChanged: boolean): boolean {
    const accountId = this.normalizeAccountId(id);

    if (!accountId || this.userAccounts.indexOf(accountId) === -1) {
      return false;
    }

    if (this.selectedAccount.value === accountId) {
      localStorage.setItem(this.accountKey, accountId);
      return true;
    }

    localStorage.setItem(this.accountKey, accountId);
    this.selectedAccount.next(accountId);

    if (emitAccountChanged) {
      this.clearAccountScopedSessionStorage();
      this.accountChangedSubject.next(accountId);
    }

    return true;
  }

  private getClaimValues(claimsList: JwtClaims[], claimNames: string[]): any[] {
    const values = [];

    claimsList.forEach(claims => {
      claimNames.forEach(claimName => {
        if (claims[claimName] !== undefined && claims[claimName] !== null) {
          values.push(claims[claimName]);
        }
      });
    });

    return values;
  }

  private normalizeAccountIds(values: any[]): string[] {
    return this.distinct(this.normalizeStringValues(values)
      .map(value => this.normalizeAccountId(value))
      .filter(value => this.isValidAccountId(value)));
  }

  private normalizeStringValues(values: any[]): string[] {
    const normalizedValues = [];

    const addValue = (value: any) => {
      if (value === undefined || value === null) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(addValue);
        return;
      }

      if (typeof value === 'object') {
        addValue(value.AccountID ?? value.accountID ?? value.accountId ?? value.account_id ?? value.id ?? value.ID ?? value.value);
        return;
      }

      const stringValue = String(value).trim();
      if (!stringValue) {
        return;
      }

      if (stringValue.startsWith('[') || stringValue.startsWith('{')) {
        try {
          addValue(JSON.parse(stringValue));
          return;
        } catch {
        }
      }

      stringValue.split(',')
        .map(item => item.trim())
        .filter(item => !!item)
        .forEach(item => normalizedValues.push(item));
    };

    values.forEach(addValue);

    return normalizedValues;
  }

  private normalizeAccountId(value: string): string {
    const accountId = `${value || ""}`.trim();

    return this.isValidAccountId(accountId) ? accountId : "";
  }

  private isValidAccountId(value: string): boolean {
    const accountId = `${value || ""}`.trim().toLowerCase();

    return !!accountId && accountId !== 'undefined' && accountId !== 'null';
  }

  private hasStoredAccount(value: string): boolean {
    return value !== undefined && value !== null && `${value}`.trim() !== "";
  }

  private distinct(values: string[]): string[] {
    return values.filter((value, index) => values.indexOf(value) === index);
  }

  private clearAccountScopedSessionStorage(): void {
    accountScopedSessionStorageKeys.forEach(key => sessionStorage.removeItem(key));
  }

  private clearAccountStorage(): void {
    localStorage.removeItem(this.accountKey);
    this.clearAccountScopedSessionStorage();
  }
}
